import { Check, X } from 'lucide-react';
import { CategoryChip } from './CategoryChip';
import { RewardPill } from './RewardPill';
import { CoinIcon } from './icons';
import type { QuestCategory, QuestState } from './types';

export interface QuestCardProps {
  state: QuestState;
  category: QuestCategory;
  /** Localized category name (chip a11y). */
  categoryLabel: string;
  title: string;
  /** Caption meta line, e.g. `QR quest · ends 17:00` / `Starts 14:00` / `Expired`. */
  meta?: string;
  /** Bucks reward for active/locked/expired/processing cards. */
  reward?: number;
  /** Accessible name for the reward pill, e.g. t('ec2.a11y.bucksReward', {amount}). */
  rewardLabel?: string;
  /** Pill text while processing, e.g. `⏳ ~30s`. */
  processingText?: string;
  /** Caption while processing, e.g. `Checking your photo…` (rendered in --accent). */
  processingCaption?: string;
  /** Amount earned when solved (defaults to `reward`). */
  earned?: number;
  /** One-line failure reason, e.g. `64/70%`. */
  failedReason?: string;
  /** SR text for the solved check / failed cross. */
  solvedLabel?: string;
  failedLabel?: string;
  onClick?: () => void;
  className?: string;
}

/**
 * Quest card (brief §6 "Home / quest list"), all states:
 * - active: full surface card — chip, 21px title, caption meta, gold reward pill.
 * - processing: full card, shimmer on the pill, caption in --accent; tap = view submission.
 * - locked/expired: full card at 55% opacity; chip keeps its hue; title stays AA-readable.
 * - solved/failed: collapse to 56px compact rows (✓ +200 gold / ✗ + reason, tappable).
 */
export function QuestCard(props: QuestCardProps) {
  const { state, onClick, className = '' } = props;
  const interactive = Boolean(onClick);
  const Tag: 'button' | 'article' = interactive ? 'button' : 'article';
  const tagProps = interactive ? ({ type: 'button', onClick } as const) : ({} as const);

  const base =
    'w-full text-left rounded-(--radius-card) border border-border bg-surface ' +
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ' +
    'transition-opacity duration-(--duration-slow)';

  if (state === 'solved' || state === 'failed') {
    return (
      <Tag
        {...tagProps}
        data-testid="ec-quest-card"
        data-state={state}
        className={`${base} flex h-14 items-center gap-3 px-4 py-0 ${className}`}
      >
        <CategoryChip category={props.category} label={props.categoryLabel} className="size-8 rounded-[9px] [&>svg]:size-4" />
        <span className="min-w-0 flex-1 truncate font-display text-base font-[650] text-fg">{props.title}</span>
        {state === 'solved' ? (
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <Check size={18} className="text-success" aria-hidden />
            {props.solvedLabel && <span className="sr-only">{props.solvedLabel}</span>}
            <span className="inline-flex items-center gap-1 font-display text-sm font-[650] text-bucks tabular-nums">
              <CoinIcon size={14} aria-hidden />
              +{props.earned ?? props.reward ?? 0}
            </span>
          </span>
        ) : (
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <X size={18} className="text-danger" aria-hidden />
            {props.failedLabel && <span className="sr-only">{props.failedLabel}</span>}
            {props.failedReason && <span className="text-[13px] font-medium text-fg-secondary">{props.failedReason}</span>}
          </span>
        )}
      </Tag>
    );
  }

  const dimmed = state === 'locked' || state === 'expired';
  const processing = state === 'processing';

  return (
    <Tag
      {...tagProps}
      data-testid="ec-quest-card"
      data-state={state}
      className={`${base} p-4 ${dimmed ? 'opacity-55' : ''} ${className}`}
    >
      <div className="flex items-start gap-3">
        <CategoryChip category={props.category} label={props.categoryLabel} />
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 font-display text-[21px] leading-[1.25] font-[650] text-fg">{props.title}</h3>
          {props.meta && <p className="mt-0.5 text-[13px] font-medium text-fg-secondary">{props.meta}</p>}
          {processing && props.processingCaption && (
            <p className="mt-0.5 text-[13px] font-medium text-accent">{props.processingCaption}</p>
          )}
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        {processing ? (
          <RewardPill text={props.processingText ?? '…'} shimmer label={props.rewardLabel ?? props.processingText ?? '…'} />
        ) : (
          props.reward !== undefined && <RewardPill amount={props.reward} label={props.rewardLabel ?? `+${props.reward}`} />
        )}
      </div>
    </Tag>
  );
}
